// Le thème des graphiques de l'observatoire : palettes, encres, police, règle d'axes.
//
// Par défaut, ce sont EXACTEMENT les valeurs que ChartBlock écrivait en dur jusqu'au
// 11/10/2026 : le site ne change pas d'un pixel (test themeGraphiques.test.ts). Seules les
// maquettes vivantes de /admin/design fournissent un autre thème, par le contexte, pour
// juger une direction de design sur nos vrais graphiques — et c'est aussi le point d'entrée
// d'un futur mode sombre (un thème de plus).
import { createContext, useContext } from 'react'

export interface ThemeGraphiques {
  /** Séries 2 à 8 d'un graphique multi-séries ; la série 1 prend la couleur de la thématique. */
  seriesSuivantes: string[]
  /** Parts d'un anneau. */
  anneau: string[]
  /** Hommes, femmes. */
  pyramide: [string, string]
  /** Contour de comparaison de la pyramide. */
  contour: string
  grille: string
  /** Encre des étiquettes-valeurs posées à côté des barres et des points. */
  encre: string
  /** Encre de la mention N/D. */
  encreND: string
  /** Gris conventionnel des séries « strate ». */
  strate: string
  /** Remplace la couleur d'une thématique (sa couleur d'origine en entrée). */
  couleurTheme?: (couleur: string) => string
  /** Famille des graduations, de la légende et des étiquettes ; absente, celle du site. */
  police?: string
  /**
   * Règle de Marc (11/10/2026) : un axe de valeurs va de juste sous le minimum à juste
   * au-dessus du maximum, barres comprises, pour ne pas écraser des valeurs proches
   * (900 à 1 000 sur un axe qui part de zéro). Une marque signale alors l'axe tronqué.
   */
  axesAjustes: boolean
}

export const THEME_GRAPHIQUES_SITE: ThemeGraphiques = {
  seriesSuivantes: ['#0EA5E9', '#F59E0B', '#7C3AED', '#10B981', '#EF4444', '#EC4899', '#64748B'],
  anneau: ['#0047FF', '#E84B00', '#00A843', '#7700DD', '#0099BB', '#E68000', '#1A00BB', '#008C44'],
  pyramide: ['#3B6FE0', '#E06AA8'],
  contour: '#0D1F3C',
  grille: '#EEF1F5',
  encre: '#334155',
  encreND: '#94A3B8',
  strate: '#94A3B8',
  axesAjustes: false,
}

export const ThemeGraphiquesCtx = createContext<ThemeGraphiques>(THEME_GRAPHIQUES_SITE)

export function useThemeGraphiques(): ThemeGraphiques {
  return useContext(ThemeGraphiquesCtx)
}

/**
 * Le bas d'un axe ajusté, ou `null` s'il faut garder le zéro : valeurs négatives, plage
 * nulle, ou valeurs déjà proches de zéro (le minimum sous 20 % du maximum — couper l'axe
 * n'y gagnerait presque rien et ferait croire à un écart qui n'existe pas).
 */
export function basAxeAjuste(min: number, max: number): number | null {
  if (!(min > 0) || !(max > min) || min < 0.2 * max) return null
  const brut = min - 0.15 * (max - min)
  const pas = 10 ** (Math.floor(Math.log10(Math.max(max - min, 1e-9))) - 1)
  return Math.max(0, Math.floor(brut / pas) * pas)
}
